import { PayoutMethodEnum } from "enum/common";
import { ReactComponent as PaypalPreviewIcon } from "assets/PayPalPreview.svg";
import { ReactComponent as VenmoPreviewIcon } from "assets/VenmoPreview.svg";
import { ReactComponent as CheckPreviewIcon } from "assets/CheckPreview.svg";
import { ReactComponent as EFTPreviewIcon } from "assets/EFTPreview.svg";
import { ReactComponent as ZellePreviewIcon } from "assets/ZellePreview.svg";
import { PayoutMethodType } from "containers/PayoutManagement/types";
import PayoutPopoverOptions from "components/PayoutManagement/PayoutPopoverOptions";
import { useMemo, useState } from "react";
import { MoreVertical20Filled } from "@fluentui/react-icons";
import ZellePreview from "components/PayoutManagement/previews/ZellePreview";
import VenmoPreview from "components/PayoutManagement/previews/VenmoPreview";
import CheckPreview from "components/PayoutManagement/previews/CheckPreview";
import toast from "react-hot-toast";
import { http } from "middleware/apiMiddleware";
import { deletePayoutMethod } from "constants/apiUrls";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { RootState } from "store";
import CanadianEFTPreview from "components/PayoutManagement/previews/CanadianEFTPreview";
import { setPayoutMethods } from "store/slices/payoutSlice";
import { Loader } from "components/Common/Loader";
import Render from "components/Common/Render";
import ConfirmationModal from "components/Common/ConfirmationModal";
import DeletePayoutConfirmation from "components/PayoutManagement/forms/DeletePayoutConfirmation";

const PayoutMethodPreview: React.FC<{
  loading: boolean;
  defaultPayoutMethod: PayoutMethodType | null;
  setPaymentModal: Function;
  setIsBankForm: (value: boolean) => void;
  afterSubmit: () => void;
  showPopooverOptions?: boolean;
}> = ({
  defaultPayoutMethod,
  setPaymentModal,
  setIsBankForm,
  afterSubmit,
  loading,
  showPopooverOptions = true,
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingPayoutMethod, setDeletingPayoutMethod] = useState(false);
  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );

  const onClose = () => setOpen(false);
  const onHideDeleteConfirmationModal = () => setShowDeleteConfirmation(false);

  const getPayoutIcons = (type?: PayoutMethodEnum) => {
    switch (type) {
      case PayoutMethodEnum.PAYPAL:
        return <PaypalPreviewIcon />;
      case PayoutMethodEnum.ZELLE:
        return <ZellePreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.VENMO:
        return <VenmoPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.CHECK:
        return <CheckPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.CANADA_EFT:
        return <EFTPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.DEFAULT:
        return null;
    }
  };

  const payoutMethodPreview = useMemo(() => {
    switch (defaultPayoutMethod?.type) {
      case PayoutMethodEnum.PAYPAL:
      case PayoutMethodEnum.ZELLE:
        return <ZellePreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.VENMO:
        return <VenmoPreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.CHECK:
        return <CheckPreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.CANADA_EFT:
        return <CanadianEFTPreview defaultPayoutMethod={defaultPayoutMethod} />;
      default:
        return <>Type doesn't match</>;
    }
  }, [defaultPayoutMethod]);

  const handleEdit = () => {
    setIsBankForm(false);
    setOpen(false);
    setPaymentModal({
      type: "edit",
      method: defaultPayoutMethod?.type,
      data: defaultPayoutMethod,
    });
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const onDelete = async () => {
    try {
      setOpen(false);
      setDeletingPayoutMethod(true);
      const { data, error } = await http.delete(
        deletePayoutMethod(selectedBusiness?.uuid || "")
      );

      if (!error) {
        dispatch(setPayoutMethods(null));
        afterSubmit();
        toast.success("Payout method deleted successfully");
      } else {
        toast.error(
          error?.error || "Error occurred while deleting payout method"
        );
      }
    } catch (error) {
      toast("Unexpected error occusred while deleting payout method");
    } finally {
      setDeletingPayoutMethod(false);
      onHideDeleteConfirmationModal();
    }
  };

  const handleChange = () => {
    setOpen(false);
    setPaymentModal({ data: null, type: "add" });
  };

  if (loading) {
    return <Loader size={6} />;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 w-full overflow-visible">
      <div className="flex-shrink-0 flex items-center justify-center">
          {getPayoutIcons(defaultPayoutMethod?.type)}
        </div>
      <div className="flex-1 flex flex-col gap-1">
        {payoutMethodPreview}
      </div>
      <Render when={showDeleteConfirmation}>
        <ConfirmationModal
          loading={deletingPayoutMethod}
          show={showDeleteConfirmation}
          onConfirmClick={onDelete}
          onHide={onHideDeleteConfirmationModal}
          renderConfirmationMessage={<DeletePayoutConfirmation />}
          yesButtonLabel="Yes, Delete"
          noButtonLabel="No, Cancel"
          customClass="md:w-[30vw] w-[70vw] !md:m-0 !p-0 !z-50 h-auto"
        />
      </Render>
      <Render when={showPopooverOptions}>
        <div className="flex-shrink-0 relative">
          <div
            className="flex items-center justify-center cursor-pointer"
            onClick={() => setOpen(true)}
          >
            <MoreVertical20Filled
              fontSize={20}
              className="text-gray-600"
            />
          </div>
          <Render when={open}>
            <>
              {/* Desktop: Dropdown */}
              <div className="hidden md:block">
                <div
                  className="fixed inset-0 z-30"
                  onClick={() => {
                    setOpen(false);
                    onClose();
                  }}
                />
                <div
                  className="absolute right-0 top-8 z-40 w-[262px] rounded-[4px] bg-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.14)] py-2 px-4"
                >
              <PayoutPopoverOptions
                setOpen={setOpen}
                onClose={onClose}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleChange={handleChange}
              />
                </div>
              </div>

              {/* Mobile: Bottom Sheet */}
              <div className="md:hidden">
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
                  onClick={() => {
                    setOpen(false);
                    onClose();
                  }}
                />
                {/* Bottom Sheet */}
                <div
                  className="fixed bottom-0 left-0 right-0 z-[9999] bg-white rounded-t-[16px] py-6 px-4"
                >
                  <div className="flex flex-col gap-2">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="font-poppins font-medium text-[18px] leading-[100%] text-[#0B1215]">
                        Saved Payout Method
                      </h2>
                      <button
                        onClick={() => {
                          setOpen(false);
                          onClose();
                        }}
                        className="hover:opacity-70 transition-opacity"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="#0B1215" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>

                    {/* Subtitle */}
                    <p className="font-poppins font-normal text-[12px] leading-[100%] text-[#7D7D7D] mb-4">
                      What would you like to do?
                    </p>

                    {/* Options */}
                    <div className="flex flex-col">
                      {/* Edit */}
                      <div
                        className="flex items-center gap-3 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-[#F3F4F6]"
                        onClick={() => {
                          handleEdit();
                          setOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.7586 5.73165L14.268 2.24024C14.1519 2.12414 14.0141 2.03204 13.8624 1.9692C13.7107 1.90637 13.5482 1.87402 13.384 1.87402C13.2198 1.87402 13.0572 1.90637 12.9056 1.9692C12.7539 2.03204 12.6161 2.12414 12.5 2.24024L2.86641 11.8746C2.74983 11.9903 2.65741 12.1279 2.59451 12.2796C2.5316 12.4313 2.49948 12.594 2.50001 12.7582V16.2496C2.50001 16.5811 2.6317 16.8991 2.86612 17.1335C3.10054 17.3679 3.41849 17.4996 3.75001 17.4996H7.24141C7.40563 17.5001 7.5683 17.468 7.71999 17.4051C7.87168 17.3422 8.00935 17.2498 8.12501 17.1332L17.7586 7.49962C17.8747 7.38354 17.9668 7.24573 18.0296 7.09405C18.0925 6.94238 18.1248 6.77981 18.1248 6.61563C18.1248 6.45146 18.0925 6.28889 18.0296 6.13721C17.9668 5.98554 17.8747 5.84772 17.7586 5.73165ZM7.24141 16.2496H3.75001V12.7582L10.625 5.88321L14.1164 9.37462L7.24141 16.2496ZM15 8.49024L11.5086 4.99962L13.3836 3.12462L16.875 6.61524L15 8.49024Z" fill="#0B1215"/>
                        </svg>
                        <span className="font-poppins text-[14px] text-[#0B1215]">Edit</span>
                      </div>

                      {/* Change */}
                      <div
                        className="flex items-center gap-3 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-[#F3F4F6]"
                        onClick={() => {
                          handleChange();
                          setOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.5 3.75V11.875C17.5 12.2065 17.3683 12.5245 17.1339 12.7589C16.8995 12.9933 16.5815 13.125 16.25 13.125H7.75859L8.56719 13.9328C8.62526 13.9909 8.67132 14.0598 8.70275 14.1357C8.73417 14.2116 8.75035 14.2929 8.75035 14.375C8.75035 14.4571 8.73417 14.5384 8.70275 14.6143C8.67132 14.6902 8.62526 14.7591 8.56719 14.8172C8.50912 14.8753 8.44018 14.9213 8.36431 14.9527C8.28844 14.9842 8.20712 15.0003 8.125 15.0003C8.04288 15.0003 7.96156 14.9842 7.88569 14.9527C7.80982 14.9213 7.74088 14.8753 7.68281 14.8172L5.80781 12.9422C5.7497 12.8841 5.7036 12.8152 5.67215 12.7393C5.6407 12.6635 5.62451 12.5821 5.62451 12.5C5.62451 12.4179 5.6407 12.3365 5.67215 12.2607C5.7036 12.1848 5.7497 12.1159 5.80781 12.0578L7.68281 10.1828C7.80009 10.0655 7.95915 9.99965 8.125 9.99965C8.29085 9.99965 8.44991 10.0655 8.56719 10.1828C8.68446 10.3001 8.75035 10.4591 8.75035 10.625C8.75035 10.7909 8.68446 10.9499 8.56719 11.0672L7.75859 11.875H16.25V3.75H7.5V4.375C7.5 4.54076 7.43415 4.69973 7.31694 4.81694C7.19973 4.93415 7.04076 5 6.875 5C6.70924 5 6.55027 4.93415 6.43306 4.81694C6.31585 4.69973 6.25 4.54076 6.25 4.375V3.75C6.25 3.41848 6.3817 3.10054 6.61612 2.86612C6.85054 2.6317 7.16848 2.5 7.5 2.5H16.25C16.5815 2.5 16.8995 2.6317 17.1339 2.86612C17.3683 3.10054 17.5 3.41848 17.5 3.75ZM13.125 15C12.9592 15 12.8003 15.0658 12.6831 15.1831C12.5658 15.3003 12.5 15.4592 12.5 15.625V16.25H3.75V8.125H12.2414L11.4328 8.93281C11.3155 9.05009 11.2497 9.20915 11.2497 9.375C11.2497 9.54085 11.3155 9.69991 11.4328 9.81719C11.5501 9.93446 11.7091 10.0003 11.875 10.0003C12.0409 10.0003 12.1999 9.93446 12.3172 9.81719L14.1922 7.94219C14.2503 7.88414 14.2964 7.81521 14.3279 7.73934C14.3593 7.66346 14.3755 7.58213 14.3755 7.5C14.3755 7.41787 14.3593 7.33654 14.3279 7.26066C14.2964 7.18479 14.2503 7.11586 14.1922 7.05781L12.3172 5.18281C12.1999 5.06554 12.0409 4.99965 11.875 4.99965C11.7091 4.99965 11.5501 5.06554 11.4328 5.18281C11.3155 5.30009 11.2497 5.45915 11.2497 5.625C11.2497 5.79085 11.3155 5.94991 11.4328 6.06719L12.2414 6.875H3.75C3.41848 6.875 3.10054 7.0067 2.86612 7.24112C2.6317 7.47554 2.5 7.79348 2.5 8.125V16.25C2.5 16.5815 2.6317 16.8995 2.86612 17.1339C3.10054 17.3683 3.41848 17.5 3.75 17.5H12.5C12.8315 17.5 13.1495 17.3683 13.3839 17.1339C13.6183 16.8995 13.75 16.5815 13.75 16.25V15.625C13.75 15.4592 13.6842 15.3003 13.5669 15.1831C13.4497 15.0658 13.2908 15 13.125 15Z" fill="#0B1215"/>
                        </svg>
                        <span className="font-poppins text-[14px] text-[#0B1215]">Change</span>
                      </div>

                      {/* Delete */}
                      <div
                        className="flex items-center gap-3 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => {
                          handleDelete();
                          setOpen(false);
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16.875 3.75H13.125V3.125C13.125 2.62772 12.9275 2.15081 12.5758 1.79917C12.2242 1.44754 11.7473 1.25 11.25 1.25H8.75C8.25272 1.25 7.77581 1.44754 7.42417 1.79917C7.07254 2.15081 6.875 2.62772 6.875 3.125V3.75H3.125C2.95924 3.75 2.80027 3.81585 2.68306 3.93306C2.56585 4.05027 2.5 4.20924 2.5 4.375C2.5 4.54076 2.56585 4.69973 2.68306 4.81694C2.80027 4.93415 2.95924 5 3.125 5H3.75V16.25C3.75 16.5815 3.8817 16.8995 4.11612 17.1339C4.35054 17.3683 4.66848 17.5 5 17.5H15C15.3315 17.5 15.6495 17.3683 15.8839 17.1339C16.1183 16.8995 16.25 16.5815 16.25 16.25V5H16.875C17.0408 5 17.1997 4.93415 17.3169 4.81694C17.4342 4.69973 17.5 4.54076 17.5 4.375C17.5 4.20924 17.4342 4.05027 17.3169 3.93306C17.1997 3.81585 17.0408 3.75 16.875 3.75ZM8.125 3.125C8.125 2.95924 8.19085 2.80027 8.30806 2.68306C8.42527 2.56585 8.58424 2.5 8.75 2.5H11.25C11.4158 2.5 11.5747 2.56585 11.6919 2.68306C11.8092 2.80027 11.875 2.95924 11.875 3.125V3.75H8.125V3.125ZM15 16.25H5V5H15V16.25ZM8.75 7.5V13.75C8.75 13.9158 8.68415 14.0747 8.56694 14.1919C8.44973 14.3092 8.29076 14.375 8.125 14.375C7.95924 14.375 7.80027 14.3092 7.68306 14.1919C7.56585 14.0747 7.5 13.9158 7.5 13.75V7.5C7.5 7.33424 7.56585 7.17527 7.68306 7.05806C7.80027 6.94085 7.95924 6.875 8.125 6.875C8.29076 6.875 8.44973 6.94085 8.56694 7.05806C8.68415 7.17527 8.75 7.33424 8.75 7.5ZM12.5 7.5V13.75C12.5 13.9158 12.4342 14.0747 12.3169 14.1919C12.1997 14.3092 12.0408 14.375 11.875 14.375C11.7092 14.375 11.5503 14.3092 11.4331 14.1919C11.3158 14.0747 11.25 13.9158 11.25 13.75V7.5C11.25 7.33424 11.3158 7.17527 11.4331 7.05806C11.5503 6.94085 11.7092 6.875 11.875 6.875C12.0408 6.875 12.1997 6.94085 12.3169 7.05806C12.4342 7.17527 12.5 7.33424 12.5 7.5Z" fill="#FF5630"/>
                        </svg>
                        <span className="font-poppins text-[14px] text-[#FF5630]">Delete</span>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
            </>
          </Render>
        </div>
      </Render>
    </div>
  );
};

export default PayoutMethodPreview;
